import {
  CalendarIcon,
  ChartBarIcon,
  CogIcon,
  CubeIcon,
  HomeIcon,
  MapIcon,
  ShoppingBagIcon,
  UsersIcon,
} from '@heroicons/react/solid'
import { useActiveFeatureFlags, useCurrentSite } from 'contexts/SiteConfig'
import { useMemo } from 'react'
import {
  CUSTOMERS_VIEW,
  INVENTORY_VIEW,
  REPORTS_VIEW,
  RESERVATIONS_VIEW,
  SETTINGS_VIEW,
} from 'constants/permissions'
import { useTranslation } from 'react-i18next'

export const useSidebarLinks = () => {
  const { t } = useTranslation()
  const featureFlags = useActiveFeatureFlags()
  const { appointment_enquiries_enabled: enquiriesEnabled } = useCurrentSite()

  return useMemo(() => {
    const links = [
      {
        to: '/',
        heroIcon: HomeIcon,
        title: 'Home',
        exact: true,
      },
      {
        to: featureFlags.includes('new_orders_list') ? '/beta/orders' : '/orders',
        heroIcon: ShoppingBagIcon,
        title: 'Orders',
        permissions: [RESERVATIONS_VIEW],
        disabledReason: t('tooltip.missing_permission.reservations_view'),
        childLinks: [
          {
            to: featureFlags.includes('new_orders_list') ? '/beta/orders' : '/orders',
            title: 'All orders',
            exact: true,
            disabledReason: t('tooltip.missing_permission.reservations_view'),
          },
          enquiriesEnabled && {
            to: '/orders/enquiries',
            title: 'Enquiries',
            disabledReason: t('tooltip.missing_permission.reservations_view'),
          },
          {
            to: '/dashboards/pay-by-link',
            title: 'Pay by link',
            disabledReason: t('tooltip.missing_permission.reservations_view'),
          },
          {
            to: '/vouchers',
            title: 'Vouchers',
            disabledReason: t('tooltip.missing_permission.reservations_view'),
          },
        ].filter(Boolean),
      },
      {
        to: '/visits',
        heroIcon: MapIcon,
        title: 'Visits',
        permissions: [RESERVATIONS_VIEW],
        disabledReason: t('tooltip.missing_permission.reservations_view'),
      },
      {
        to: '/calendar',
        heroIcon: CalendarIcon,
        title: 'Calendar',
        permissions: [RESERVATIONS_VIEW],
        disabledReason: t('tooltip.missing_permission.reservations_view'),
        childLinks: [
          {
            to: '/calendar',
            title: 'Diary',
            exact: true,
            disabledReason: t('tooltip.missing_permission.reservations_view'),
          },
          {
            to: '/calendar/schedule',
            title: 'Schedule',
            disabledReason: t('tooltip.missing_permission.reservations_view'),
          },
          {
            to: '/practitioner-availability',
            title: 'Rota',
            disabledReason: t('tooltip.missing_permission.reservations_view'),
          },
        ],
      },
      {
        to: '/clients',
        heroIcon: UsersIcon,
        title: 'Clients',
        permissions: [CUSTOMERS_VIEW],
        disabledReason: t('tooltip.missing_permission.clients_view'),
        childLinks: [
          {
            to: '/clients',
            title: 'Client profiles',
            disabledReason: t('tooltip.missing_permission.clients_view'),
          },
          {
            to: '/memberships',
            title: 'Memberships',
            disabledReason: t('tooltip.missing_permission.clients_view'),
          },
          featureFlags.includes('marketing') && {
            to: '/emails',
            title: 'Emails',
            isBeta: true,
            disabledReason: t('tooltip.missing_permission.clients_view'),
          },
        ].filter(Boolean),
      },
      {
        to: '/inventory/stock-items',
        heroIcon: CubeIcon,
        title: 'Inventory',
        permissions: [INVENTORY_VIEW],
        disabledReason: t('tooltip.missing_permission.inventory_view'),
        childLinks: [
          {
            to: '/inventory/stock-items',
            title: 'Stock items',
            disabledReason: t('tooltip.missing_permission.inventory_view'),
          },
          {
            to: '/inventory/stock-orders',
            title: 'Stock orders',
            disabledReason: t('tooltip.missing_permission.inventory_view'),
          },
          {
            to: '/inventory/stock-takes',
            title: 'Stocktakes',
            disabledReason: t('tooltip.missing_permission.inventory_view'),
          },
        ],
      },
      {
        to: '/reports',
        heroIcon: ChartBarIcon,
        title: 'Reports',
        permissions: [REPORTS_VIEW],
        disabledReason: t('tooltip.missing_permission.reports_view'),
      },
      {
        to: '/settings',
        title: 'Settings',
        permissions: [SETTINGS_VIEW],
        heroIcon: CogIcon,
        disabledReason: t('tooltip.missing_permission.settings_view'),
      },
    ].filter(Boolean)

    return links
      .filter(
        ({ featureFlag = null, visible = true }) =>
          visible !== false && (!featureFlag || featureFlags.includes(featureFlag))
      )
      .map((link) => ({
        ...link,
        childLinks: link.childLinks?.map((child) => ({
          ...child,
        })),
      }))
  }, [enquiriesEnabled, featureFlags, t])
}
